import { useState, useRef, useEffect } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sparkles, Send } from "lucide-react";

const SUGGESTIONS = [
  "What's the GST registration threshold for freelancers in India?",
  "How does Section 44ADA presumptive taxation work?",
  "Old vs New tax regime — which one for me?",
  "Can I claim ITC on my MacBook?",
  "What invoices need e-invoicing in 2026?",
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const r = await api.post("/ai/chat", { session_id: sessionId, message: msg });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I had trouble responding. Please try again." }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 h-full flex flex-col" data-testid="ai-page">
      <div>
        <p className="tc-overline mb-2">Powered by Claude Sonnet 4.5</p>
        <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold tracking-tight">AI Co-pilot</h1>
        <p className="text-sm text-[#5a5a53] mt-2 max-w-xl">
          Trained for the Indian freelancer's tax brain. Ask about GST, income tax, ITC, advance tax, or invoicing.
        </p>
      </div>

      <div className="tc-card flex-1 flex flex-col min-h-[60vh] max-h-[75vh]">
        <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="ai-messages">
          {messages.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-[#5a5a53]">Try one of these to get started:</p>
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => send(s)} data-testid={`ai-suggestion-${i}`}
                    className="px-3 py-2 text-xs text-left border border-[#e5e5e0] rounded-md hover:bg-[#f4f1eb] hover:border-[#0a0a0a] transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] px-4 py-3 rounded-md text-sm whitespace-pre-wrap leading-relaxed ${
                m.role === "user"
                  ? "bg-[#0a0a0a] text-[#fafaf8]"
                  : "bg-[#f4f1eb] text-[#0a0a0a] border border-[#e5e5e0]"
              }`}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex">
              <div className="bg-[#f4f1eb] border border-[#e5e5e0] px-4 py-3 rounded-md text-sm text-[#5a5a53] flex items-center gap-2">
                <Sparkles size={14} className="text-[#d95d39]" />
                <span className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-[#d95d39] rounded-full animate-pulse" />
                  <span className="w-1.5 h-1.5 bg-[#d95d39] rounded-full animate-pulse" style={{ animationDelay: "0.2s" }} />
                  <span className="w-1.5 h-1.5 bg-[#d95d39] rounded-full animate-pulse" style={{ animationDelay: "0.4s" }} />
                </span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-[#e5e5e0] p-4 flex items-center gap-3"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about GST, income tax, invoicing..."
            className="flex-1 h-11 rounded-md border-[#e5e5e0] focus-visible:ring-[#d95d39]"
            data-testid="ai-input"
            disabled={loading}
          />
          <Button type="submit" disabled={loading || !input.trim()} data-testid="ai-send-btn"
            className="h-11 bg-[#d95d39] hover:bg-[#c84c35] text-white rounded-md">
            <Send size={16} />
          </Button>
        </form>
      </div>
    </div>
  );
}
